class CanvasController {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.img = null;

    this.scale = 1;
    this.coverScale = 1;
    this.offset = { x: 0, y: 0 };
    this.isDragging = false;
    this.startPos = { x: 0, y: 0 };
    
    this.pointers = new Map();
    this.lastTouchDist = null;

    this.zoomInput = null;
    this.zoomConfig = {
      min: 0,
      max: 1,
      step: 0
    };

    this.options = Object.assign({
      clampMargin: 1,
      onDraw: null,
      defaultSize: { width: CANVAS_SIZE, height: CANVAS_SIZE }
    }, options);

    this.setCanvasSize(this.options.defaultSize.width, this.options.defaultSize.height);
    this.setupListeners();
  }

  // --- Public API ---

  /**
   * Loads an image from file input and draws it to canvas.
   */
  loadFromFile(file, cb) {
    const reader = new FileReader();

    reader.onload = () => {
      const img = new Image();

      img.onload = () => {
        this.img = img;
        this.setInitialCoverScale();
        this.renderImage();
        cb?.();
      };

      img.onerror = () => {
        console.error("Failed to load image");
      };

      img.src = reader.result;
    };

    reader.onerror = () => {
      console.error("Failed to read file");
    };

    reader.readAsDataURL(file);
  }

  /**
   * Exports current canvas as Blob via callback.
   */
  async toBlob() {
    return new Promise((resolve, reject) => {
      this.canvas.toBlob(blob => {
        if (blob) return resolve(blob);
  
        // Fallback: Safari or other browsers without WebP support
        this.canvas.toBlob(fallback => {
          if (fallback) resolve(fallback);
          else reject(new Error('Failed to create blob (both WebP and PNG failed)'));
        }, 'image/png');
      }, 'image/webp', 0.85);
    });
  }   

  /**
   * Updates current zoom scale and redraws image.
   */
  zoomTo(newScale) {
    if (!this.img) return;

    newScale = parseFloat(newScale) || 1;

    const prevScale = this.scale;
    const ratio = newScale / prevScale;

    // Keep image centered relative to current offset
    this.offset.x *= ratio;
    this.offset.y *= ratio;

    this.scale = newScale;
    this.clampOffset();
    this.renderImage();
  }

  /**
   * Applies scale relative to cover fit.
   */
  zoomRelativeToCover(factor) {
    if (!this.coverScale || !this.img) return;
    this.zoomTo(this.coverScale * factor);
  }

  // --- Initialization & Events ---

  /**
   * Sets up pointer events and window blur.
   */
  setupListeners() {
    this.canvas.addEventListener('pointerdown', this.onPointerDown.bind(this));
    window.addEventListener('blur', this.stopDragging.bind(this));
    this.canvas.addEventListener('pointerup', e => this.pointers.delete(e.pointerId));
    this.canvas.addEventListener('pointercancel', e => this.pointers.delete(e.pointerId));
  }

  /**
   * Called when pointer pressed on canvas.
   */
  onPointerDown(e) {
    e.preventDefault();
    if (this.pointers.size >= 2) return;
    this.pointers.set(e.pointerId, this.getPointerPosition(e));
    this.isDragging = true;
    this.canvas.classList.add(this.options.classNames.move);
    this.startPos = this.getPointerPosition(e);

    this._onMove = this.onPointerMove.bind(this);
    this._onUp = this.stopDragging.bind(this);
    this._onStop = this.stopDragging.bind(this);

    document.body.addEventListener('pointermove', this._onMove);
    document.body.addEventListener('pointerup', this._onUp);
    document.body.addEventListener('pointercancel', this._onUp);
    window.addEventListener("mouseup", this._onStop);
  }

  /**
   * Called when pointer moves — applies dragging.
   */
  onPointerMove(e) {
    if (!this.isDragging) return;

    if (!this.pointers.has(e.pointerId)) return;    
    this.pointers.set(e.pointerId, this.getPointerPosition(e));

    if (this.pointers.size === 2 && this.zoomInput) {
      const [p1, p2] = [...this.pointers.values()];
      const dx = p1.x - p2.x;
      const dy = p1.y - p2.y;
      const dist = Math.hypot(dx, dy);

      if (this.lastTouchDist != null) {
        const delta = dist / this.lastTouchDist - 1;
        if(!delta) return;
        const newDelta = delta > 0 ? 1 : -1; // sensitivity
        const current = parseFloat(this.zoomInput.value);
        const newVal = current + newDelta * this.zoomConfig.step;
        this.setZoomInputValue(newVal);
      }

      this.lastTouchDist = dist;
      return;
    }


    const current = this.getPointerPosition(e);
    const scaleX = this.canvas.width / this.canvas.getBoundingClientRect().width;
    const scaleY = this.canvas.height / this.canvas.getBoundingClientRect().height;

    const dx = (current.x - this.startPos.x) * scaleX;
    const dy = (current.y - this.startPos.y) * scaleY;

    this.offset.x += dx;
    this.offset.y += dy;
    this.startPos = current;

    this.clampOffset();
    this.renderImage();
  }

  /**
   * Also clears drag state on mouseup (fallback).
   */
  stopDragging(e) {
    this.isDragging = false;
    this.pointers.clear();
    this.lastTouchDist = null;
    
    this.canvas.classList.remove(this.options.classNames.move);
    window.removeEventListener("mouseup", this._onStop);
    document.body.removeEventListener('pointermove', this._onMove);
    document.body.removeEventListener('pointerup', this._onUp);
    document.body.removeEventListener('pointercancel', this._onUp);
    this.options.onStop?.();
  }

  /**
   * Should be called externally. Requires a range input.
   * Adds a wheel event to the canvas to control zoom via the input.
   */
  attachZoomWithWheel(input) {
    this.attachZoomInput(input)
  
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      if (e.deltaY === 0 || Math.abs(e.deltaY) < 1) return;
  
      const delta = e.deltaY > 0 ? -1 : 1;
      const current = parseFloat(input.value);
      const newVal = current + delta * this.zoomConfig.step;
      this.setZoomInputValue(newVal);
    }, { passive: false })
  }   

  attachZoomInput(input) {
    this.zoomInput = input;
    this.zoomConfig = {
      min: parseFloat(input.min) || 0.1,
      max: parseFloat(input.max) || 10,
      step: parseFloat(input.step) || 0.1
    };
  
    this.zoomTo(parseFloat(input.value));
  }  

  setZoomInputValue(val) {
    if (!this.zoomInput) return;
  
    const { min, max } = this.zoomConfig;
    val = Math.min(max, Math.max(min, val));
    this.zoomInput.value = val.toFixed(2);
    this.zoomInput.dispatchEvent(new Event('input'));
  }  

  /**
   * Returns pointer coordinates relative to canvas.
   */
  getPointerPosition(e) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    };
  }

  // --- Drawing & Layout ---

  /**
   * Resizes canvas to new dimensions.
   */
  setCanvasSize(w, h) {
    this.canvas.width = w;
    this.canvas.height = h;
  }

  /**
   * Calculates initial scale to fully cover canvas.
   */
  setInitialCoverScale() {
    if (!this.img) return;
    const scaleX = this.canvas.width / this.img.width;
    const scaleY = this.canvas.height / this.img.height;
    this.coverScale = Math.max(scaleX, scaleY);
    this.scale = this.coverScale;
    this.offset = { x: 0, y: 0 };
  }

  /**
   * Redraws the image on canvas at the center.
   */

  resetPosition(){
    this.offset = { x: 0, y: 0 };
    this.renderImage();
  }


  /**
   * Draws the image on canvas at current scale and offset.
   */
  renderImage() {
    if (!this.img) return;

    const { ctx, canvas, img, scale, offset } = this;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    const imgW = img.width * scale;
    const imgH = img.height * scale;
    const x = (canvas.width - imgW) / 2 + offset.x;
    const y = (canvas.height - imgH) / 2 + offset.y;

    ctx.drawImage(img, x, y, imgW, imgH);

    if (typeof this.options.onDraw === 'function') {
      this.options.onDraw(this.scale);
    }
  }

  /**
   * Ensures the image does not move too far beyond canvas.
   */
  clampOffset() {
    if (!this.img) return;

    const margin = this.options.clampMargin;
    const imgW = this.img.width * this.scale;
    const imgH = this.img.height * this.scale;

    const minX = -imgW + margin;
    const maxX = this.canvas.width - margin;
    const minY = -imgH + margin;
    const maxY = this.canvas.height - margin;

    const x = (this.canvas.width - imgW) / 2 + this.offset.x;
    const y = (this.canvas.height - imgH) / 2 + this.offset.y;

    if (x > maxX) this.offset.x = maxX - (this.canvas.width - imgW) / 2;
    if (x < minX) this.offset.x = minX - (this.canvas.width - imgW) / 2;
    if (y > maxY) this.offset.y = maxY - (this.canvas.height - imgH) / 2;
    if (y < minY) this.offset.y = minY - (this.canvas.height - imgH) / 2;
  }
}

export default CanvasController;